import AnalyticsWebClient, {
	originTracingType,
	originType,
} from '@atlassiansox/analytics-web-client';
import OriginTracer from '@atlassiansox/origin-tracing';

import { fg } from '@atlaskit/platform-feature-flags';
import { getIdentityEnv, getMappedEnv } from '@atlassian/atlassian-studio-env-utils';
import { isStudioV2 } from '@atlassian/atlassian-studio-routing';

const env = getMappedEnv();
const product = 'atlassianStudio';

const getGasApiHost = () => {
	const currentDomain = window.location.hostname;

	if (fg('studio-fix-staging-analytics-destination')) {
		switch (env) {
			case 'staging':
			case 'production':
				return `${currentDomain}/gateway/api/gasv3/api/v1`;
			case 'test':
			case 'local':
				return 'atlassian-studio.stg-east.frontend.public.atl-paas.net/gateway/api/gasv3/api/v1';
			default:
				return 'atlassian-studio.stg-east.frontend.public.atl-paas.net/gateway/api/gasv3/api/v1';
		}
	}

	switch (env) {
		case 'staging':
			return 'team.stg.atlassian.com/gateway/api/gasv3/api/v1';
		case 'production':
			return `${currentDomain}/gateway/api/gasv3/api/v1`;
		case 'test':
		case 'local':
			return 'team.stg.atlassian.com/gateway/api/gasv3/api/v1';
		default:
			return 'team.stg.atlassian.com/gateway/api/gasv3/api/v1';
	}
};

export const initAnalyticsClient = (): AnalyticsWebClient => {
	const client = new AnalyticsWebClient(
		{
			env: getIdentityEnv(),
			origin: originType.WEB,
			product,
			subproduct: originType.WEB,
			version: isStudioV2() ? 'v2' : 'v1',
		},
		{
			apiHost: getGasApiHost(),
			// https://hello.atlassian.net/wiki/spaces/policy/pages/3303839825/Onboarding+a+Product+to+the+Cookies+Library+atlassian+browser-storage-controls#Upgrade-Analytics-Client-version-and-include-disableCookiePersistence-variable
			disableCookiePersistence: true,
		},
	);

	// Set up origin tracing to capture the `atlOrigin` query parameter on landing.
	// Fire "origin landed" event
	// https://developer.atlassian.com/platform/analytics-pipeline/guides/deep-dive/task-sessions/#why-use-the-analytics-web-client-for-origin-tracing
	if (fg('asf-1372-origin-tracking-analytic-studio')) {
		client.setOriginTracingHandlers({
			[originTracingType.ATL_ORIGIN]: (encodedOrigin: string) => {
				const origin = OriginTracer.fromEncoded(encodedOrigin);

				return {
					originTracingAttributes: origin.toAnalyticsAttributes(),
					...(!origin.isMalformed() && { taskSessionId: origin.id }),
				};
			},
		});
	}

	return client;
};

export enum UIActionSubject {
	PICKER = 'picker',
}

export enum Action {
	CLICKED = 'clicked',
	OPENED = 'opened',
	SEARCH = 'searched',
	PAGINATE = 'paginated',
	SELECTED = 'selected',
}

export type ActionSubject = string;

export const getTrackEventName = (actionSubject: ActionSubject, action: Action) =>
	`${actionSubject} ${action}`;

export const getUIEventName = (actionSubject: UIActionSubject, action: Action) =>
	`${actionSubject} ${action}`;
